import * as angular from 'angular';

import { TemplateCreationDto, ChangesToApplyDTO } from '../../models/DTO';
import { Group, Template, DifferenceList } from '../../models';
import { IRequestService } from '../request.service';

export interface ITemplateService {
    getAll(): angular.IPromise<Template[]>;
    getAll(force: boolean): angular.IPromise<Template[]>;
    createTemplate(name: string, comment: string, groupGuid: string, includeChildren: boolean): angular.IPromise<Template>;
    createGroup(templateGuid: string, parentGroupGuid: string): angular.IPromise<Group>;
    createGroup(templateGuid: string, parentGroupGuid: string, groupName: string): angular.IPromise<Group>;
    delete(templateGuid: string): angular.IPromise<any>;
    getAllChanges(): angular.IPromise<DifferenceList>;
    countAllChanges(): angular.IPromise<{ [guid: string]: number }>;
    applyChanges(changes: ChangesToApplyDTO[]): angular.IPromise<DifferenceList>;
    getUsages(templateGuid: string): angular.IPromise<any>;
    applyAllFromTemplate(templateGuid: string): angular.IPromise<any>;
    ignoreAllFromTemplate(templateGuid: string): angular.IPromise<any>;
    applyAllForGroup(groupGuid: string): angular.IPromise<any>;
    cleanChangesCache(): void;
}

export class TemplateService implements ITemplateService {
    static serviceId = 'templateService';

    static $inject = ['requestService'];

    constructor(
        private _requestService: IRequestService) {
    }

    private controllerUrl(): string {
        const url = this._requestService.getUrlV1() + 'template/';
        return url;
    }

    private cleanCacheGroup(guid?: string): void {
        const url = `${this._requestService.getUrlV1()}group/`;
        this._requestService.cleanCache(url);
        if (guid) {
            this._requestService.cleanCache(url + guid);
        }
    }

    getAll(force: boolean = false): angular.IPromise<Template[]> {
        return this._requestService.get(this.controllerUrl());
    }

    createTemplate(name: string, comment: string, groupGuid: string, includeChildren: boolean): angular.IPromise<Template> {
        const url = this.controllerUrl();
        this._requestService.cleanCache(url);
        this.cleanCacheGroup(groupGuid);
        const dto: TemplateCreationDto = {
            name: name,
            comment: comment,
            groupGuid: groupGuid,
            includeChildren: includeChildren
        };
        this._requestService.cleanCacheAtV1(this.controllerUrl() + 'changes/count');
        return this._requestService.post(url, dto);
    }

    createGroup(templateGuid: string, parentGroupGuid: string, groupName?: string): angular.IPromise<Group> {
        let url = this.controllerUrl() + `create/${templateGuid}/${parentGroupGuid}`;
        if (groupName) {
            url = `${url}/${groupName}`;
        }
        return this._requestService.post(url)
            .then(data => {
                this._requestService.cleanCacheAtV1('group/steps');
                this.cleanCacheGroup();
                return data;
            });
    }

    delete(templateGuid: string): angular.IPromise<any> {
        const url = this.controllerUrl() + `delete/${templateGuid}`;
        this._requestService.cleanCache(this.controllerUrl());
        this.cleanCacheGroup();
        return this._requestService.post(url);
    }

    getAllChanges(): angular.IPromise<DifferenceList> {
        const url = this.controllerUrl() + 'changes/all';
        return this._requestService.get(url);
    }

    countAllChanges(): angular.IPromise<{ [guid: string]: number }> {
        const url = this.controllerUrl() + 'changes/count';
        return this._requestService.get(url);
    }

    cleanChangesCache(): void {
        this._requestService.cleanCache(this.controllerUrl() + 'changes/count');
        this._requestService.cleanCache(this.controllerUrl() + 'changes/all');
    }

    applyChanges(changes: ChangesToApplyDTO[]): angular.IPromise<DifferenceList> {
        const url = this.controllerUrl() + 'changes';
        return this._requestService.post(url, changes).then(data => {
            this._requestService.cleanAllCache();
            return data;
        });
    }

    getUsages(templateGuid: string): angular.IPromise<any> {
        const url = `${this.controllerUrl()}usages/${templateGuid}`;
        return this._requestService.get(url);
    }

    applyAllFromTemplate(templateGuid: string): angular.IPromise<any> {
        return this._requestService.post(`${this.controllerUrl()}applyAllFromTemplate/${templateGuid}`)
            .then((data) => {
                this._requestService.cleanAllCache();
                return data;
            });
    }

    ignoreAllFromTemplate(templateGuid: string): angular.IPromise<any> {
        return this._requestService.post(`${this.controllerUrl()}ignoreAllFromTemplate/${templateGuid}`)
            .then((data) => {
                this._requestService.cleanAllCache();
                return data;
            });
    }

    applyAllForGroup(groupGuid: string): angular.IPromise<any> {
        return this._requestService.post(`${this.controllerUrl()}applyAllForGroup/${groupGuid}`)
            .then((data) => {
                this._requestService.cleanAllCache();
                return data;
            });
    }

}
